import { Level, Stage, Module, LevelStatus } from '../types';

export const levels: Level[] = [
  // ==========================================
  // 新生儿期 - 喂养模块
  // ==========================================
  {
    id: 'newborn-feeding-1',
    stage: Stage.NEWBORN,
    module: Module.FEEDING,
    title: '宝宝是不是饿了？',
    description: '学会识别新生儿饥饿信号、掌握配方奶粉正确冲调方法，避免只用哭声或固定时间表判断喂养。',
    estimatedTime: 5,
    status: LevelStatus.IN_PROGRESS,
    order: 1,
    knowledgePoints: [
      '哭往往是较晚的饥饿信号，提早观察转头、张嘴、吸吮动作等早期信号',
      '喂养间隔因人而异，观察宝宝状态比死守时间表更重要',
      '配方奶粉需严格按比例冲调：1平勺配30ml水，冲好的奶1小时内食用完毕',
      '持续拒奶、精神差等异常需及时就医',
    ],
    riskWarning:
      '若宝宝精神反应差、吃奶明显减少、持续拒奶或伴随发热/脱水表现（囟门凹陷、尿量显著减少），应及时咨询医生。',
  },
  {
    id: 'newborn-feeding-2',
    stage: Stage.NEWBORN,
    module: Module.FEEDING,
    title: '拍嗝与吐奶',
    description: '掌握拍嗝时机与瓶喂正确姿势，学会区分正常溢奶和需要就医的异常呕吐。',
    estimatedTime: 8,
    status: LevelStatus.NOT_STARTED,
    order: 2,
    knowledgePoints: [
      '喂奶过程中和喂完后都可以拍嗝，帮助排出吞入的空气',
      '少量吐奶（溢奶）在新生儿期非常常见，不等于生病',
      '喷射状呕吐、呕吐物颜色异常或伴有精神差需立即就医',
      '正确的瓶喂姿势：宝宝半竖立45°，奶瓶倾斜使奶嘴充满奶液，减少吞入空气',
    ],
    riskWarning:
      '若出现喷射状呕吐、呕吐物呈黄绿色或带血、频繁吐奶且伴有精神萎靡或体重增长不良，应立即就医。',
  },
  {
    id: 'newborn-feeding-3',
    stage: Stage.NEWBORN,
    module: Module.FEEDING,
    title: '判断是否吃够',
    description: '掌握尿量、体重增长两大客观指标，科学评估喂养是否充足。',
    estimatedTime: 10,
    status: LevelStatus.NOT_STARTED,
    order: 3,
    knowledgePoints: [
      '每天 6-8 片湿透的尿布是判断奶量充足的重要指标',
      '新生儿出生后前几天体重下降 7%-10% 属正常现象',
      '恢复出生体重后，前 3 个月每天约增长 14-28 克',
      '判断是否吃饱要看整体趋势（尿量+体重+精神状态），而非单次称重',
    ],
    riskWarning:
      '若出生 2 周后仍低于出生体重、每天湿尿布少于 4-6 片、或体重持续下降，应及时就医评估。',
  },

  // ==========================================
  // 新生儿期 - 睡眠模块
  // ==========================================
  {
    id: 'newborn-sleep-1',
    stage: Stage.NEWBORN,
    module: Module.SLEEP,
    title: '安全睡姿',
    description: '掌握仰卧睡眠原则和安全睡眠环境配置，降低婴儿猝死综合征（SIDS）风险。',
    estimatedTime: 6,
    status: LevelStatus.NOT_STARTED,
    order: 1,
    knowledgePoints: [
      '始终让新生儿仰卧入睡，可将 SIDS 风险降低 50% 以上',
      '婴儿床上不放置枕头、毛绒玩具、厚被子等松软物品',
      '推荐使用睡袋替代被子，与父母同室但不同床至少到 6 个月',
      '床垫需坚硬且与婴儿床内部尺寸完全匹配',
    ],
    riskWarning:
      '婴儿床上不应有任何可能遮挡口鼻的松软物品。若宝宝在睡眠中出现呼吸异常或肤色异常，应立即就医。',
  },
  {
    id: 'newborn-sleep-2',
    stage: Stage.NEWBORN,
    module: Module.SLEEP,
    title: '夜醒应对',
    description: '理解新生儿睡眠周期特点，掌握科学的夜醒排查顺序。',
    estimatedTime: 7,
    status: LevelStatus.NOT_STARTED,
    order: 2,
    knowledgePoints: [
      '新生儿睡眠周期约 50-60 分钟，浅睡眠比例高，频繁醒来是正常发育现象',
      '夜醒原因很多：尿布湿了、太冷太热、需要安抚，不一定每次都要喂奶',
      '正确的排查顺序：观察状态 → 检查尿布/温度 → 判断是否饥饿 → 安抚',
      '夜间护理保持安静、光线暗，避免过度刺激',
    ],
    riskWarning:
      '若宝宝在夜间异常安静不动、难以唤醒，或伴有呼吸异常、发热等症状，应立即就医。',
  },
  {
    id: 'newborn-sleep-3',
    stage: Stage.NEWBORN,
    module: Module.SLEEP,
    title: '昼夜节律',
    description: '学习帮宝宝区分白天和晚上的方法，建立固定的睡前程序。',
    estimatedTime: 5,
    status: LevelStatus.NOT_STARTED,
    order: 3,
    knowledgePoints: [
      '新生儿昼夜节律尚未成熟，需要照护者帮助建立',
      '白天保持正常光线、互动玩耍；夜间保持昏暗安静、减少刺激',
      '建立固定的睡前流程（如洗澡→喂奶→抚触→昏暗环境）',
      '通常到 3-4 个月后昼夜节律逐渐稳定',
    ],
    riskWarning:
      '如果宝宝长期日夜颠倒严重影响家庭作息，或伴随其他发育迟缓表现，可咨询儿科医生排除潜在问题。',
  },

  // ==========================================
  // 新生儿期 - 日常护理模块
  // ==========================================
  {
    id: 'newborn-care-1',
    stage: Stage.NEWBORN,
    module: Module.DAILY_CARE,
    title: '脐带护理',
    description: '掌握脐带自然脱落的护理原则，学会识别感染的警示信号。',
    estimatedTime: 5,
    status: LevelStatus.NOT_STARTED,
    order: 1,
    knowledgePoints: [
      '脐带残端通常在出生后 1-3 周自然脱落，保持清洁干燥即可',
      '现代指南不推荐用酒精擦拭脐带，过度消毒反而可能延迟脱落',
      '脱落过程中少量出血（几滴）是正常现象',
      '脐周皮肤发红、肿胀、有黄色脓性分泌物提示可能感染',
    ],
    riskWarning:
      '若脐带超过 3 周未脱落，或出现脐周红肿、黄色/绿色脓性带异味分泌物、触碰时宝宝哭闹抗拒、伴有发热，应立即就医。',
  },
  {
    id: 'newborn-care-2',
    stage: Stage.NEWBORN,
    module: Module.DAILY_CARE,
    title: '洗澡',
    description: '掌握脐带脱落前的擦浴方法和盆浴安全原则，学会控制水温与洗澡频率。',
    estimatedTime: 8,
    status: LevelStatus.NOT_STARTED,
    order: 2,
    knowledgePoints: [
      '脐带未脱落/未完全愈合前只做擦浴，避免脐带浸泡在水中',
      '水温以手腕内侧感觉温热（约37-38℃）为宜，水深不超过 5 厘米',
      '接水时先放冷水后放热水，关闭时先关热水后关冷水',
      '绝对不能将宝宝独自留在水中，哪怕只有几秒钟',
      '1 岁以下婴儿每周用沐浴液洗 3 次即可，过于频繁会使皮肤干燥',
    ],
    riskWarning:
      '若发生烫伤或溺水意外，请立即拨打急救电话。如发现皮肤出现异常红疹、脓疱或大面积脱皮，应及时就医。家用热水器最高温度建议设在 49℃ 以下。',
  },
  {
    id: 'newborn-care-3',
    stage: Stage.NEWBORN,
    module: Module.DAILY_CARE,
    title: '换尿布',
    description: '学习正确的换尿布流程，掌握尿布疹预防和排泄物观察方法。',
    estimatedTime: 5,
    status: LevelStatus.NOT_STARTED,
    order: 3,
    knowledgePoints: [
      '大便后应立即更换尿布，大便中的消化酶会侵蚀婴儿皮肤',
      '预防尿布疹三原则：勤换尿布、保持干爽、适当通风',
      '已出现尿布疹时使用含氧化锌的护臀膏保护皮肤屏障',
      '健康新生儿每天一般尿湿 4-6 片以上尿布',
      '出生后第 1 周尿布上粉色/红褐色痕迹多为尿酸盐结晶，非出血',
    ],
    riskWarning:
      '若尿布疹持续加重、出现脓疱或破溃、涂护臀膏 2-3 天无效，需就医排除酵母菌感染。尿液中有真正血迹或伴有发热/食欲不振应及时就医。',
  },

  // ==========================================
  // 新生儿期 - 安全防护模块
  // ==========================================
  {
    id: 'newborn-safety-1',
    stage: Stage.NEWBORN,
    module: Module.SAFETY,
    title: '防呛奶',
    description: '了解呛奶原因与预防方法，掌握呛奶发生时的正确处理步骤。',
    estimatedTime: 6,
    status: LevelStatus.NOT_STARTED,
    order: 1,
    knowledgePoints: [
      '呛奶常见原因：喂奶姿势不当、奶流速过快（奶嘴孔过大）',
      '瓶喂时选择小孔奶嘴，奶瓶适当倾斜确保奶嘴充满奶液',
      '轻度呛咳（能咳嗽/哭/呼吸）：立即停止喂养，让宝宝前倾靠自身咳嗽排出，不要用力拍背',
      '重度窒息（无法出声/呼吸、面色发青）：面部朝下+头低于躯干+5次背击+5次胸压，同时拨打急救电话',
      '禁止竖直抱起拍背（会使异物更深），禁止将手指伸入口中抠取',
      '喂奶后竖抱或保持半坐姿势 20-30 分钟，减少溢奶和呛奶风险',
    ],
    riskWarning:
      '若呛奶后出现持续咳嗽、呼吸急促或困难、口唇或面色发青，提示奶液可能进入气道，应立即拨打急救电话。',
  },
  {
    id: 'newborn-safety-2',
    stage: Stage.NEWBORN,
    module: Module.SAFETY,
    title: '防跌落',
    description: '了解新生儿跌落风险场景，掌握婴儿床安全标准和抱娃行走安全规范。',
    estimatedTime: 5,
    status: LevelStatus.NOT_STARTED,
    order: 2,
    knowledgePoints: [
      '永远不要将宝宝独自留在尿布台、床、沙发等任何高于地面的台面上',
      '婴儿床护栏栏杆间距需小于 6 厘米，床垫与床架紧密匹配不留缝隙',
      '婴儿床不要放在窗户旁，窗帘拉绳等带状物应远离婴儿床',
      '抱着宝宝上下楼梯时务必扶着扶手，不要一手抱娃一手拿东西',
    ],
    riskWarning:
      '若宝宝从任何高度跌落（尤其是头部着地），应立即检查意识状态，如有呕吐、嗜睡、异常哭闹或反应迟钝应立即就医。',
  },
  {
    id: 'newborn-safety-3',
    stage: Stage.NEWBORN,
    module: Module.SAFETY,
    title: '安全抱姿',
    description: '掌握正确的抱娃姿势，了解摇晃婴儿综合征的危害和婴儿背带安全使用规范。',
    estimatedTime: 4,
    status: LevelStatus.NOT_STARTED,
    order: 3,
    knowledgePoints: [
      '抱起新生儿时一只手托住头颈、另一只手托住臀部',
      '任何情况下都不要用力摇晃婴儿，可导致脑血管撕裂和永久性脑损伤',
      '使用婴儿背带遵循 T.I.C.K.S 原则：紧贴、可见、可亲吻、下巴不贴胸、背部支撑',
      '感到情绪激动时先把宝宝安全放下、自己冷静后再回来安抚',
    ],
    riskWarning:
      '摇晃婴儿综合征可造成永久性严重脑损伤甚至死亡。若怀疑宝宝曾被剧烈摇晃，立即就医。',
  },

  // ==========================================
  // 新生儿期 - 发育观察模块
  // ==========================================
  {
    id: 'newborn-dev-1',
    stage: Stage.NEWBORN,
    module: Module.DEVELOPMENT,
    title: '黄疸观察',
    description: '了解新生儿黄疸的原因和生理过程，掌握居家观察方法和危险信号识别。',
    estimatedTime: 7,
    status: LevelStatus.NOT_STARTED,
    order: 1,
    knowledgePoints: [
      '黄疸是因血液中胆红素升高导致皮肤和眼白发黄，大多数新生儿黄疸是生理性的',
      '在自然光下用手指轻压皮肤观察底色，黄疸从头到脚发展、从脚到头消退',
      '出生后 2-4 天出现的轻度黄疸且精神吃奶正常多为生理性黄疸',
      '出生 24 小时内出现、迅速发展、或伴有精神萎靡需立即就医',
      '严重黄疸的标准治疗是光疗（蓝光照射），晒太阳不能替代',
    ],
    riskWarning:
      '若出生 24 小时内出现黄疸、黄疸迅速加重至下半身、宝宝精神萎靡/嗜睡/吸吮无力、大便颜色变浅呈陶土色，需立即就医。',
  },
  {
    id: 'newborn-dev-2',
    stage: Stage.NEWBORN,
    module: Module.DEVELOPMENT,
    title: '体重变化',
    description: '理解新生儿生理性体重下降的正常范围，掌握体重增长的合理预期和警示信号。',
    estimatedTime: 5,
    status: LevelStatus.NOT_STARTED,
    order: 2,
    knowledgePoints: [
      '出生后前几天体重下降 7%-10% 以内是正常的生理现象',
      '健康足月新生儿一般在出生后 7-10 天恢复到出生体重',
      '恢复体重后，前 3 个月每天约增长 14-28 克（每周约 100-200 克）',
      '判断体重增长关键是看整体趋势，而非单次称重数据',
      '体重增长不良的信号：2 周未恢复出生体重、尿布减少、精神差',
    ],
    riskWarning:
      '若体重下降超过 10%、出生 2 周后仍低于出生体重、体重持续不增或下降、伴有精神萎靡或喂养困难，应及时就医评估。',
  },

  // ==========================================
  // 1-3 个月 - 喂养模块
  // ==========================================
  {
    id: 'stage1-feeding-1',
    stage: Stage.ONE_TO_THREE,
    module: Module.FEEDING,
    title: '喂养节奏的变化',
    description: '理解满月后胃容量增大、单次奶量增加和夜间喂奶减少的变化规律，掌握猛长期的喂养应对方法。',
    estimatedTime: 5,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '满月后宝宝胃容量增大，单次吃奶量显著增加，喂奶间隔自然拉长',
      '夜间喂奶次数逐步减少，2个月时夜间约吃1-2次即可',
      '配方奶消化较慢约3-4小时，满月后喂奶间隔自然拉长',
      '猛长期出现在第3-6周，配方奶宝宝可能需要每次多加10-20ml，通常持续2-3天',
      '猛长期按需喂养但尊重宝宝饱腹信号，避免过度喂养',
    ],
    riskWarning:
      '若宝宝持续拒奶、体重增长明显放缓、每天湿尿布少于4片或伴随精神萎靡，应及时就医。',
  },
  {
    id: 'stage1-feeding-2',
    stage: Stage.ONE_TO_THREE,
    module: Module.FEEDING,
    title: '喂养误区与边界',
    description: '了解1-3个月宝宝绝对不能吃的食物，学会识别"用喂奶做唯一安抚"的误区和正确判断吃饱的方法。',
    estimatedTime: 6,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '1-3个月宝宝只能吃配方奶，任何情况下都不要喂果汁或固体食物',
      '吐舌反射在2-4个月仍很强，宝宝会将固体食物自动推出而非吞咽',
      '不要用喂奶作为唯一的安抚方式——宝宝哭也可能是尿布湿了、太热太冷、需要安抚',
      '判断吃饱要综合：吃奶状态+尿便情况+体重增长，不能只用时间表来判断',
      '宝宝6个月之前不要喂果汁，即使满6个月也要用杯子而非奶瓶，每天不超过120毫升',
    ],
    riskWarning:
      '若宝宝吃奶后频繁大量吐奶、伴随烦躁哭闹、体重增长不良，需就医排除胃食管反流或其他消化道问题。',
  },
  {
    id: 'stage1-feeding-3',
    stage: Stage.ONE_TO_THREE,
    module: Module.FEEDING,
    title: '吐奶升级与胃食管反流',
    description: '学会区分正常的生理性溢奶和需要就医的胃食管反流（GERD），掌握减少吐奶的实用技巧。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '生理性溢奶（少量吐奶）在3个月以内仍然常见，因贲门肌肉尚未发育成熟',
      '胃食管反流（GERD）的信号：频繁大量吐奶、喂奶时弓背哭闹、喂后烦躁、体重增长不良',
      '减少吐奶的技巧：少量多次喂养、喂后竖抱20-30分钟、喂奶中拍嗝1-2次',
      '大多数生理性溢奶随月龄增长（4-6个月后）自然好转',
      '喷射性呕吐、呕吐物呈黄绿色或带血、伴有精神萎靡需立即就医',
    ],
    riskWarning:
      '出现喷射性呕吐（奶液射出而非溢出）、呕吐物呈黄绿色或带血、宝宝弓背哭闹拒食或体重不增，应立即就医。',
  },

  // ==========================================
  // 1-3 个月 - 睡眠模块
  // ==========================================
  {
    id: 'stage1-sleep-1',
    stage: Stage.ONE_TO_THREE,
    module: Module.SLEEP,
    title: '睡眠规律的形成',
    description: '了解1-3个月宝宝睡眠时长和节律的变化，帮助宝宝逐渐建立规律的睡眠模式。',
    estimatedTime: 5,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '1-3个月宝宝全天睡眠约15-16小时，2个月时夜间可连续睡5-6小时',
      '3个月大时许多宝宝夜间可连续睡6-8小时（但不等于睡整夜，中间可能短暂醒）',
      '宝宝白天清醒时间变长，安静清醒期是最好的互动学习时间',
      '神经系统成熟和胃容量增大是睡眠变长的生理基础',
      '继续保持白天明亮有互动、夜间昏暗安静的节律引导',
    ],
    riskWarning:
      '若宝宝在睡眠中异常安静不动难以唤醒，或出现呼吸暂停、面色发青，应立即就医。',
  },
  {
    id: 'stage1-sleep-2',
    stage: Stage.ONE_TO_THREE,
    module: Module.SLEEP,
    title: 'SIDS预防升级',
    description: '第2-3个月是婴儿猝死综合征（SIDS）发生风险最高的时期，必须严格执行安全睡眠要求。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '婴儿猝死综合征（SIDS）在第2-3个月发生率最高，这是最需要警惕的时期',
      'SIDS风险因素：趴睡、床上松软物品、父母吸烟、过热、早产、男婴风险略高',
      '仰卧睡眠不可妥协——即使宝宝看起来睡得更"香"，趴睡可显著增加SIDS风险',
      '与父母同室不同床至少持续到6个月（最好到1年），可降低SIDS风险达50%',
      '使用安抚奶嘴已被证明可降低SIDS风险，建议喂养规律稳定后（约3-4周）再引入',
    ],
    riskWarning:
      'SIDS无法完全预防，但严格执行安全睡眠措施可将风险降至最低。若发现宝宝在睡眠中呼吸异常或面色改变，立即就医。',
  },
  {
    id: 'stage1-sleep-3',
    stage: Stage.ONE_TO_THREE,
    module: Module.SLEEP,
    title: '睡眠倒退与应对',
    description: '理解发育波动期导致的暂时性睡眠倒退，学会区分正常的发育起伏和需要警惕的问题。',
    estimatedTime: 6,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '发育波动期宝宝可能暂时出现睡眠倒退——之前能睡5-6小时，突然又每2小时醒',
      '睡眠倒退可能是新技能学习的前兆（如宝宝在"默默练习"翻身）',
      '猛长期也会干扰睡眠：宝宝夜间吃奶需求骤增，通常持续2-3天后自行恢复',
      '正常的睡眠倒退是暂时的（数天到1-2周），宝宝醒来精神状态良好',
      '需要警惕的信号：睡眠倒退持续超过2周、伴有体重增长不良或精神萎靡',
    ],
    riskWarning:
      '若睡眠倒退持续超过2周且伴有喂养困难、体重增长不良或宝宝白天持续烦躁萎靡，应咨询儿科医生。',
  },

  // ==========================================
  // 1-3 个月 - 日常护理模块
  // ==========================================
  {
    id: 'stage1-care-1',
    stage: Stage.ONE_TO_THREE,
    module: Module.DAILY_CARE,
    title: '皮肤与身体变化',
    description: '了解满月后宝宝皮肤状态的正常变化，掌握新生儿皮肤病自然消退的规律和黄疸最终阶段的观察。',
    estimatedTime: 5,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '新生儿痤疮（婴儿粉刺）通常在1-2个月内自然消退，每周用清水轻柔清洗即可，不要挤',
      '粟粒疹等新生儿常见皮疹大多在2-3个月自然消失',
      '黄疸一般在2个月内应基本消退——若2个月后仍明显发黄，必须就医',
      '宝宝脸颊变得肉嘟嘟、胳膊和腿更加丰满，这是正常的生长表现',
      '头皮乳痂（摇篮帽）可用温和婴儿洗发水清洗后轻轻刷去，通常数周内自行消退',
    ],
    riskWarning:
      '若出现皮肤大面积红疹、脓疱、水疱或破溃，或黄疸持续超过2个月仍明显不退、大便颜色异常变浅，应立即就医。',
  },
  {
    id: 'stage1-care-2',
    stage: Stage.ONE_TO_THREE,
    module: Module.DAILY_CARE,
    title: '洗澡与日常卫生升级',
    description: '宝宝1个月后可以盆浴了，但随之而来的是新的安全挑战——宝宝变大了、会伸手乱够。',
    estimatedTime: 6,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '脐带完全愈合后可以盆浴，但仍需注意肚脐区域的清洁干燥',
      '1个月后宝宝可能伸手够到水龙头，应使用单独浴盆并远离水龙头',
      '水温仍保持在37-38℃（手腕内侧温热），先放冷水后放热水，热水器温度不超过49℃',
      '洗澡时绝对不能让宝宝处于无人看管状态——哪怕一秒钟都不行',
      '指甲长得更快了，手指甲约一个月剪1-2次；脚趾甲长得慢，一个月剪1次即可',
    ],
    riskWarning:
      '烫伤是1岁以下婴儿最常见的家庭意外之一。若发生烫伤，立即用流动冷水冲洗伤处至少20分钟，不要涂酱油牙膏等偏方，严重烫伤立即就医。',
  },
  {
    id: 'stage1-care-3',
    stage: Stage.ONE_TO_THREE,
    module: Module.DAILY_CARE,
    title: '外出与日常活动',
    description: '学会安全地带1-3个月宝宝外出散步和参加社交活动，掌握避免过度刺激的原则。',
    estimatedTime: 5,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '满月后可以带宝宝外出散步，选择天气温和的时段，避免极端温度',
      '避免阳光直射：1岁以下婴儿皮肤不能直接暴露在阳光下，用衣物和遮阳伞防护',
      '婴儿背带使用需遵循T.I.C.K.S原则：紧贴、可见、可亲吻、下巴不贴胸、背部支撑',
      '1-3个月宝宝可以短时间接触温和的社交环境，但要避免过度刺激',
      '过度刺激的信号：宝宝转过脸去、闭上眼睛、变得烦躁不安——此时应立即减少刺激',
    ],
    riskWarning:
      '避免带宝宝去人多拥挤、空气不流通的场所。若宝宝在户外出现异常哭闹、面色改变或精神萎靡，立即回到安静环境中观察，必要时就医。',
  },

  // ==========================================
  // 1-3 个月 - 安全防护模块
  // ==========================================
  {
    id: 'stage1-safety-1',
    stage: Stage.ONE_TO_THREE,
    module: Module.SAFETY,
    title: '防跌落新挑战',
    description: '宝宝1-3个月虽然还不能主动翻身，但已能用脚蹬着表面移动，跌落风险显著升级。',
    estimatedTime: 5,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '宝宝可用脚蹬着表面四处移动——绝不能独自留在尿布台、床、沙发等高处',
      '尿布台安全带不是"可选装"，每次换尿布都应该扣好安全带',
      '2-3个月的宝宝可能在你不注意的时候突然翻身，不要低估宝宝的活动能力',
      '汽车安全座椅中也需要系好安全带——宝宝可能从座椅中滑出或翻出',
      '如果把宝宝放在大人床上休息，必须全程在旁监护',
    ],
    riskWarning:
      '若宝宝从高处跌落（尤其是头部先着地），应立即检查意识状态。如有呕吐、嗜睡、异常哭闹、反应迟钝或瞳孔不对称，立即就医。',
  },
  {
    id: 'stage1-safety-2',
    stage: Stage.ONE_TO_THREE,
    module: Module.SAFETY,
    title: '居家环境安全升级',
    description: '随着宝宝活动能力增强，排查家中新增的安全隐患——家具、电线、小物件和玩具安全。',
    estimatedTime: 6,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '排查家具有尖锐边角的位置——宝宝虽然还不能走，但抱着时可能意外碰到',
      '电线和窗帘拉绳必须放在宝宝完全接触不到的地方，有勒颈窒息风险',
      '玩具选择标准：轻便好抓、不易吞咽、无尖锐边缘、无细小可拆卸部件',
      '适合1-3个月的玩具：硬板书、软积木、摇铃、球和挤压发声玩具',
      '塑料袋和塑料包装必须远离宝宝——容易覆盖口鼻造成窒息',
    ],
    riskWarning:
      '排查家中所有可能被宝宝够到的绳索、电线和塑料袋。若发现宝宝被绳索缠绕或塑料袋遮住口鼻，立即解除并检查呼吸，必要时拨打急救电话。',
  },
  {
    id: 'stage1-safety-3',
    stage: Stage.ONE_TO_THREE,
    module: Module.SAFETY,
    title: '窒息与烫伤预防升级',
    description: '深度了解1-3个月宝宝面临的窒息和烫伤风险，学会防范措施和紧急处理。',
    estimatedTime: 6,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '婴儿床上仍然不能放枕头、厚被子、毛绒玩具——即使在冬天也应使用睡袋而非厚被',
      '洗澡水温不超过49℃，用手腕或手肘内侧测试，先放冷水后放热水',
      '不要抱着宝宝喝热饮或吃热食——宝宝突然伸手可能导致烫伤',
      '小物件窒息风险：任何小于卷筒纸芯直径的物品都可能被宝宝吞咽',
      '窒息急救：若宝宝无法出声/呼吸/咳嗽且面色发青，立即实施背部拍击+胸部按压急救并拨打急救电话',
    ],
    riskWarning:
      '若宝宝窒息无法呼吸，立即实施婴儿窒息急救（5次背击+5次胸压交替），同时让家人拨打急救电话。烫伤后立即用流动冷水冲洗20分钟，不要涂偏方。',
  },

  // ==========================================
  // 1-3 个月 - 发育观察模块
  // ==========================================
  {
    id: 'stage1-dev-1',
    stage: Stage.ONE_TO_THREE,
    module: Module.DEVELOPMENT,
    title: '运动发育里程碑',
    description: '掌握1-3个月宝宝从头控到翻身前的运动发育轨迹，了解俯卧练习的重要性和正确方法。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '第1个月底：颈部肌肉已显著发育，俯卧时可短暂抬头并将头转向一侧',
      '第2个月：俯卧时可以抬起头向前看，而不仅仅是转向两边；仰卧时能让头保持在正中',
      '第3个月：趴着时能用前臂撑起上半身四处张望，扶着坐时能较长时间保持头部稳定',
      '宝宝从反射性动作（抓握反射、惊吓反射等）逐步过渡到有目的的自主运动',
      '定期俯卧练习（每天数次、每次几分钟）对锻炼颈部肌肉和控制头部至关重要',
      '第3个月时许多宝宝开始从背部翻到侧面再翻回来，翻身通常在3-4个月逐渐掌握',
    ],
    riskWarning:
      '每个宝宝发育节奏不同，不要盲目对照发育进度表。若3个月时仍完全不能抬头、全身松软或一侧肢体明显活动少于另一侧，应咨询儿科医生。',
  },
  {
    id: 'stage1-dev-2',
    stage: Stage.ONE_TO_THREE,
    module: Module.DEVELOPMENT,
    title: '感官与社交发育',
    description: '了解1-3个月宝宝在视觉、听觉、语言和社交情感方面的发育里程碑，学会识别发育迟缓的警示信号。',
    estimatedTime: 8,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '视觉发育：2个月时可同时移动双眼并追视缓慢物体，偏好高对比度和鲜艳色彩',
      '听觉发育：第3个月前后，听到父母声音会安静下来，听到熟悉歌曲会兴奋',
      '语言发育：6-8周开始发出咕咕声（轻柔单音节如"唔""啊"），是表达性语言的开始',
      '社交发育：6-8周出现社交性微笑——宝宝有意识地用微笑回应你的微笑',
      '6-8周是哭泣峰值（一天可哭3小时），这是正常发育阶段，到3-4个月后会显著减少',
      '这个月龄的宝宝还没有"物体永存性"概念——看不到的东西对宝宝来说就是不存在了',
    ],
    riskWarning:
      '若宝宝3个月时对巨大声音没有反应、从不注视人脸、不会追视移动物体、不发出任何咕咕声或对父母声音无回应，应咨询儿科医生评估听力或发育问题。',
  },

  // ==========================================
  // 4-6 个月 - 喂养模块
  // ==========================================
  {
    id: 'stage2-feeding-1',
    stage: Stage.FOUR_TO_SIX,
    module: Module.FEEDING,
    title: '辅食添加的时机',
    description: '学习判断宝宝是否准备好添加辅食的信号，掌握辅食添加的最佳月龄和原则。',
    estimatedTime: 8,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      'AAP建议纯母乳喂养至约6个月，辅食可在4-6个月之间开始添加',
      '挺舌反射消失是添加辅食的重要信号之一，通常在4-5个月时消失',
      '宝宝能够坐直（有支撑）、对大人食物表现出兴趣、能够将食物从勺子前部移到咽部时可以考虑添加辅食',
      '4-6个月尽早引入花生、鸡蛋等易过敏食物可降低日后过敏风险',
    ],
    riskWarning:
      '若宝宝4个月后仍持续将食物推出、不能在有支撑时坐直、或对辅食表现出强烈抗拒且伴随体重增长不良，应咨询儿科医生。不要在4个月前添加辅食，这会增加窒息和消化系统负担风险。',
  },
  {
    id: 'stage2-feeding-2',
    stage: Stage.FOUR_TO_SIX,
    module: Module.FEEDING,
    title: '辅食怎么喂',
    description: '掌握辅食的种类选择、喂养方法以及食物安全要点，了解过敏食物引入策略。',
    estimatedTime: 8,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '辅食应从单一食材开始，每次新增一种食物并观察3-5天',
      '初期辅食应是软的、泥糊状或捣碎的食物，避免添加盐和糖',
      '含铁辅食应作为优先选择：强化铁米粉、肉泥等都是好的第一口辅食',
      '坐在高脚餐椅中进食，坐直以减少食物进入呼吸道的风险',
    ],
    riskWarning:
      '若在添加某种食物后出现皮疹、呕吐、腹泻、呼吸困难或嘴唇肿胀，可能提示食物过敏，应立即停止该食物并联系医生。严重过敏反应（呼吸困难、面色发绀）需立即就医。',
  },
  {
    id: 'stage2-feeding-3',
    stage: Stage.FOUR_TO_SIX,
    module: Module.FEEDING,
    title: '母乳与辅食的平衡',
    description: '理解辅食添加期母乳或配方奶的角色变化，掌握合理的喂养组合。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '添加辅食后母乳或配方奶仍是主要营养来源，不应过早断奶',
      '辅食是"补充"而非"替代"，先喂母乳/配方奶再喂辅食或反之均可，关键看宝宝的接受度',
      '添加辅食后，母乳/配方奶摄入量可能略微减少，但每天仍需保证约700-900ml',
      '不要在辅食中加入糖、盐或蜂蜜（1岁前禁蜂蜜，有肉毒杆菌中毒风险）',
    ],
    riskWarning:
      '若添加辅食后宝宝体重明显下降、持续腹泻或便秘、或尿量显著减少（每天少于4片湿尿布），应暂停新食物并咨询医生。',
  },

  // ==========================================
  // 4-6 个月 - 睡眠模块
  // ==========================================
  {
    id: 'stage2-sleep-1',
    stage: Stage.FOUR_TO_SIX,
    module: Module.SLEEP,
    title: '睡眠规律的变化',
    description: '了解4-6月龄宝宝睡眠模式的变化，小睡次数减少，夜间睡眠时间延长。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '4-6个月时，大多数宝宝夜间连续睡眠时间可达6-8小时',
      '小睡从新生儿期的多次逐步减少到3-4次/天',
      '此阶段可能经历"4个月睡眠倒退"，这是由于睡眠周期成熟导致的正常现象',
      '继续坚持安全睡眠原则：仰卧、硬床垫、无松软物品',
    ],
    riskWarning:
      '避免用枕头、定位枕或卷起的毯子来固定宝宝睡姿，这些做法增加了窒息风险。若宝宝夜间频繁醒来且无法安抚超过2周，或伴有生长不良，应咨询儿科医生。',
  },
  {
    id: 'stage2-sleep-2',
    stage: Stage.FOUR_TO_SIX,
    module: Module.SLEEP,
    title: '睡眠倒退的应对',
    description: '识别4个月睡眠倒退的特点，学习如何帮助宝宝度过这一时期。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '4个月前后，宝宝的睡眠周期由婴儿模式转变为成人模式（深浅睡眠交替），是睡眠倒退的根本原因',
      '睡眠倒退表现为：之前能睡长觉的宝宝突然频繁夜醒、入睡困难',
      '保持一致的睡前程序有助于宝宝重新建立睡眠规律',
      '不要用喂奶作为唯一安抚手段，可以在宝宝困了但还没睡着时放入婴儿床',
    ],
    riskWarning:
      '若宝宝在睡眠倒退期出现呼吸异常（打鼾、呼吸暂停）、烦躁不安持续整夜且影响白天精神状态，需就医排查潜在健康问题。',
  },
  {
    id: 'stage2-sleep-3',
    stage: Stage.FOUR_TO_SIX,
    module: Module.SLEEP,
    title: '建立睡前程序',
    description: '学习为宝宝建立稳定的睡前程序，培养良好的睡眠习惯。',
    estimatedTime: 6,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '睡前程序应该是安静、温暖、可预测的：洗澡→换睡衣→喂奶→读绘本→关灯→轻拍',
      '睡前程序持续10-30分钟即可，过长反而让宝宝过度疲劳',
      '睡前程序中不要加入刺激性的活动（如追逐游戏、看电视）',
      '宝宝困了但还醒着时放入婴儿床，有助于他学会自主入睡',
    ],
    riskWarning:
      '不要让宝宝在沙发上或成人床上睡着后再转移，这会增加SIDS风险。始终在安全的婴儿床上让宝宝入睡。',
  },

  // ==========================================
  // 4-6 个月 - 日常护理模块
  // ==========================================
  {
    id: 'stage2-care-1',
    stage: Stage.FOUR_TO_SIX,
    module: Module.DAILY_CARE,
    title: '长牙期的护理',
    description: '了解出牙的正常表现和不适处理方法，学习从第一颗牙开始的口腔清洁。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '第一颗牙通常于4-7个月萌出（下中切牙最早），出牙顺序有个体差异',
      '出牙的正常表现：口水增多、喜欢啃咬、牙龈红肿、轻微烦躁，但不会导致高热',
      '出牙期间可以用干净的手指按摩牙龈、给牙胶或凉的湿毛巾缓解不适',
      '从第一颗牙萌出起就应开始口腔清洁：用湿纱布或软毛指套牙刷轻轻擦拭',
    ],
    riskWarning:
      '出牙本身不会引起超过38°C的发热。若宝宝发热伴烦躁、拒食，应考虑其他病因并咨询医生。不要使用含有苯佐卡因的牙胶或出牙凝胶，对婴儿有风险。',
  },
  {
    id: 'stage2-care-2',
    stage: Stage.FOUR_TO_SIX,
    module: Module.DAILY_CARE,
    title: '翻身与坐起的护理',
    description: '宝宝的行动能力变强了，学习翻身期的安全护理和正确的坐起练习方法。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '多数宝宝在5个月左右学会从俯卧翻身到仰卧，之后学会反向翻身',
      '一旦宝宝会翻身，就不要再使用尿布更换台，应在地面或大床上更换尿布',
      '帮助宝宝练习坐：用手扶住或用靠垫支撑，让他学习平衡身体',
      '当宝宝可以"三足鼎立"（身体前倾、双手撑地）时，他的核心力量正在增强',
    ],
    riskWarning:
      '从宝宝开始会翻身起，绝对不要将他单独留在床、沙发、尿布台等高处，哪怕"只离开几秒钟"。跌落是这个月龄最常见的意外伤害之一。',
  },
  {
    id: 'stage2-care-3',
    stage: Stage.FOUR_TO_SIX,
    module: Module.DAILY_CARE,
    title: '外出与社交',
    description: '掌握带4-6月龄宝宝外出的注意事项，利用社交期建立安全依恋。',
    estimatedTime: 6,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '4-6个月是宝宝的"社交期"，对陌生人露出笑容，利用此时期让宝宝熟悉照顾者',
      '外出时注意防晒：6个月以下婴儿应避免阳光直射，用衣物和帽子遮挡',
      '5-6个月时可能出现"陌生人焦虑"的前兆：对不熟悉的人会仔细审视',
      '随身携带换洗衣物、尿布和安抚物品，外出时仍要维持宝宝的日常作息',
    ],
    riskWarning:
      '在陌生环境中要格外注意宝宝的体温调节。夏季避免长时间在炎热环境，冬季注意保暖但不要过度包裹（过热增加SIDS风险）。',
  },

  // ==========================================
  // 4-6 个月 - 安全防护模块
  // ==========================================
  {
    id: 'stage2-safety-1',
    stage: Stage.FOUR_TO_SIX,
    module: Module.SAFETY,
    title: '辅食安全',
    description: '学习辅食喂养中的安全要点，防止窒息和不安全食物。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '可能导致窒息的辅食：整颗葡萄、坚果、爆米花、生胡萝卜块、热狗肠等',
      '给宝宝的食物应切成小块（<1.25cm），质地软烂，坐着进食',
      '绝不要将辅食放入奶瓶喂养——这增加窒息风险和过度喂养',
      '监督宝宝吃饭的全过程，不要让他在吃东西时玩耍或爬行',
    ],
    riskWarning:
      '学习婴儿窒息急救法（背部拍击和胸部冲击）。如果宝宝吃东西时突然无法咳嗽、哭泣或呼吸，立即实施窒息急救并拨打120。',
  },
  {
    id: 'stage2-safety-2',
    stage: Stage.FOUR_TO_SIX,
    module: Module.SAFETY,
    title: '翻身期的安全防护',
    description: '宝宝学会翻身后，家庭安全防护需要同步升级。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '检查和移除婴儿床上所有可能缠绕的物品：床围、绳子、带子等',
      '在地板上铺设安全的玩耍区域，移除周围小物件（<3cm，可放入嘴中）',
      '检查家具的稳定性：宝宝可能会拉住或推动低矮家具',
      '电器线缆应收起或固定，不要让宝宝够到',
    ],
    riskWarning:
      '家中的小物件（硬币、纽扣电池、磁铁、气球碎片）是严重的窒息和内脏损伤隐患，应从地面和低处全部清除。纽扣电池吞入可在2小时内造成严重食道灼伤。',
  },
  {
    id: 'stage2-safety-3',
    stage: Stage.FOUR_TO_SIX,
    module: Module.SAFETY,
    title: '口欲期的安全意识',
    description: '4-6个月宝宝的"口欲期"：什么都往嘴里放，安全防护怎么做。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '口欲期是正常的发育阶段：宝宝通过口腔探索世界，这有助于认知发展',
      '所有玩具应大于宝宝嘴巴（直径>4cm），不能有小零件可以拆卸',
      '定期检查玩具的完整性，破损的玩具应立刻丢弃',
      '将清洁剂、药品、化妆品等放在高处锁好的柜子里',
    ],
    riskWarning:
      '绝对不能让宝宝在无人看护的情况下接触任何可能被吞咽或吸入的物品。家中所有药物和化学品应使用儿童安全锁存放，即使放在高处也需上锁。',
  },

  // ==========================================
  // 7-12 个月 - 喂养模块
  // ==========================================
  {
    id: 'stage3-feeding-1',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.FEEDING,
    title: '辅食进阶：手指食物',
    description: '当宝宝能够自己坐稳并开始用手抓取食物时，引入手指食物和自主进食。',
    estimatedTime: 8,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '8-9个月左右，宝宝可以用拇指和食指捏起小物体（钳状抓握），这是引入手指食物的信号',
      '适合的手指食物：熟透的香蕉片、蒸软的胡萝卜条、小块的软面包、煮熟的意面',
      'BLW（宝宝自主进食）法强调让宝宝自己选择和取食物，但所有食物必须是软的、容易吞咽的',
      '6-8月龄每日喂辅食2-3次，9-12月龄每日3-4次，可加1-2次营养小点心',
    ],
    riskWarning:
      '任何手指食物都应足够软，用拇指和食指可以轻松压碎。如果食物硬到需要用牙齿咀嚼，对8-9个月的宝宝来说存在窒息风险。进食时全程监督。',
  },
  {
    id: 'stage3-feeding-2',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.FEEDING,
    title: '从奶瓶过渡到杯子',
    description: '了解何时开始、如何帮助宝宝从奶瓶过渡到使用杯子喝水或喝奶。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '6个月左右可以开始让宝宝尝试用杯子喝水，先用训练杯（鸭嘴杯或吸管杯）',
      '母乳喂养的宝宝可以直接跳过奶瓶学习使用杯子',
      '1岁时应该逐渐减少奶瓶使用，最好在12-18个月完全戒断奶瓶',
      '杯子里只放母乳/配方奶或水，不要放果汁或含糖饮料',
    ],
    riskWarning:
      '不要让宝宝抱着奶瓶入睡——奶液会残留在口腔中增加龋齿风险（"奶瓶龋"）。不要让宝宝边走边叼着奶瓶，有摔倒时口腔受伤的风险。',
  },
  {
    id: 'stage3-feeding-3',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.FEEDING,
    title: '建立三餐规律',
    description: '学习为7-12月龄宝宝建立规律的一日三餐和合理的辅食-奶量平衡。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '8-12月龄参考：早餐+午餐+晚餐，辅以1-2次小点心，母乳/配方奶仍为辅助',
      '一天的碳水化合物、蛋白质、蔬菜水果和健康脂肪要均衡分配于三餐中',
      '出牙期的食欲波动是正常的——今天不吃明天可能吃很多',
      '全家人一起进餐有助于宝宝学习餐桌礼仪和对食物的积极态度',
    ],
    riskWarning:
      '1岁前的宝宝仍"不缺铁"需要关注——应持续提供含铁丰富的食物（肉泥、深色蔬菜、强化铁的谷物）。如宝宝出现面色苍白、异常疲倦，需检查是否贫血。',
  },

  // ==========================================
  // 7-12 个月 - 睡眠模块
  // ==========================================
  {
    id: 'stage3-sleep-1',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.SLEEP,
    title: '睡眠模式的新变化',
    description: '了解7-12月龄的睡眠变化：小睡减为2次，夜间可能重新出现夜醒。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '7-9个月时小睡通常从3次减少到2次（上午和下午各一次）',
      '夜间总睡眠约10-12小时，白天小睡共约2-4小时',
      '这个阶段可能因为大运动发育（爬行、扶站）导致睡眠暂时紊乱',
      '有些宝宝会在婴儿床里练习新技能（如自己站起来），这可能导致入睡困难',
    ],
    riskWarning:
      '如果宝宝夜间呼吸声重、打鼾持续、或出现呼吸暂停（>20秒），应就医排查阻塞性睡眠呼吸暂停。这是此月龄需要关注的睡眠安全问题。',
  },
  {
    id: 'stage3-sleep-2',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.SLEEP,
    title: '分离焦虑与睡眠',
    description: '此阶段是分离焦虑高峰期，如何应对对睡眠的影响。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '8-9个月是分离焦虑的高峰期，宝宝意识到你会离开他，入睡时可能出现哭泣',
      '分离焦虑导致宝宝夜间醒来查看你是否还在身边——这是认知发育正常的标志',
      '维持平静而一致的夜间回应方式：简短安抚后离开，不要过度刺激',
      '白天多进行躲猫猫类游戏，帮助宝宝理解"看不到不等于不存在"',
    ],
    riskWarning:
      '不要在分离焦虑期使用让宝宝独自从哭到睡着的"哭声免疫法"，这会加剧焦虑。选择温和、一致的方法帮助宝宝学会自我安抚。如果宝宝持续极度痛苦且无法安抚，应咨询儿科医生排除健康问题。',
  },
  {
    id: 'stage3-sleep-3',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.SLEEP,
    title: '常见睡眠问题应对',
    description: '应对7-12月龄常见睡眠问题：频繁夜醒、早醒、拒绝小睡。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '若宝宝已经习惯喂着奶入睡或抱着入睡，他可能无法在没有这些条件时自主接觉',
      '逐步减少夜间喂养：缩短每次喂奶时长，减少量，延长喂奶间隔',
      '确保白天的小睡足够：过度疲劳会让宝宝更难入睡，形成恶性循环',
      '检查睡眠环境：房间是否太亮、太热？最佳室温约20-22°C',
    ],
    riskWarning:
      '持续无法入睡伴有抓耳、哭闹加剧，可能需要排查中耳炎。耳部感染在躺卧时症状更明显。宝宝入睡困难伴腹胀、频繁放屁或排便异常，需排查食物不耐受。',
  },

  // ==========================================
  // 7-12 个月 - 日常护理模块
  // ==========================================
  {
    id: 'stage3-care-1',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.DAILY_CARE,
    title: '牙齿护理进阶',
    description: '随着牙齿陆续萌出，学习如何正确地为宝宝刷牙和口腔健康管理。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '8个月时多数宝宝已经有2-4颗牙，牙齿清洁应成为日常习惯',
      '使用含氟牙膏：3岁以下用米粒大小（约0.1mg氟），3岁以上用豌豆大小',
      '每天刷牙两次，早晚各一次，家长帮助刷牙直到宝宝能独立完成（约6-7岁）',
      '第一次牙科检查应在第一颗牙萌出后6个月内，最迟不晚于1岁',
    ],
    riskWarning:
      '不要让宝宝含着奶瓶入睡；夜间喂奶后需用湿布擦牙或喂少许水清洁口腔。婴幼儿龋齿发展很快，一旦发现牙齿上有白斑或棕色斑点应立即就医。',
  },
  {
    id: 'stage3-care-2',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.DAILY_CARE,
    title: '爬行与活动期的护理',
    description: '爬行和扶站阶段带来全新的日常护理挑战，学习如何管理。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '爬行期的宝宝膝盖可能会有轻微摩擦痕迹，这是正常的，不需特殊处理',
      '宝宝自己站起来却不知道怎么坐下时可能会哭闹求助——教他如何屈膝慢慢坐下',
      '此阶段换尿布极具挑战性：选择在安全的地面上换，用玩具分散注意力',
      '站立扶走期脚看起来是扁平足和O形腿——这是正常的，因足弓被脂肪垫掩盖、韧带松弛',
    ],
    riskWarning:
      'O形腿在2岁内大多是正常的生理现象。但如果出现单侧O形腿、腿部明显长度不一、或弯曲程度持续加重，应就医评估。不要给婴儿使用传统的"蜡烛包"绑腿，这会影响髋关节发育。',
  },
  {
    id: 'stage3-care-3',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.DAILY_CARE,
    title: '穿衣服与换尿布新挑战',
    description: '好动的宝宝不再配合穿衣和换尿布，学习应对策略。',
    estimatedTime: 6,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '选择方便穿脱的衣物：套头衫、弹性腰带的裤子、按扣式连体衣',
      '换尿布时给宝宝一个安全的"换尿布专用玩具"转移注意力',
      '让换尿布成为互动时间：唱歌、说话、做表情，减少他的抗拒',
      '鞋子方面：在家光脚或穿防滑袜走路有助于足部发育，外出需要时选软底学步鞋',
    ],
    riskWarning:
      '不要在给宝宝穿衣服或换尿布时让他单独在高处（尿布台、床、沙发）。即使你不知道他已经能翻身了，他可能就在这一刻第一次翻身。',
  },

  // ==========================================
  // 7-12 个月 - 安全防护模块
  // ==========================================
  {
    id: 'stage3-safety-1',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.SAFETY,
    title: '爬行期的家居安全',
    description: '宝宝开始爬行，家庭安全防护必须全面升级。',
    estimatedTime: 8,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '在楼梯顶部和底部分别安装儿童安全防护门，顶部门要固定在墙上',
      '将清洁剂、药品、化妆品和尖锐物品锁在高处柜子里，不要单纯认为"宝宝够不到"',
      '盖住所有不用的电源插座，固定松散的电线和窗帘拉绳',
      '家具尖锐边角安装防撞条，电视等重物固定好防止倾倒',
    ],
    riskWarning:
      '绝对不要使用学步车！学步车不仅拖延独立行走，还使宝宝能够快速移动到达危险区域，每年造成大量跌落、烫伤和中毒事故。固定式弹跳椅或游戏架是更安全的选择。',
  },
  {
    id: 'stage3-safety-2',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.SAFETY,
    title: '站立扶走期的防跌落',
    description: '宝宝拉扶站立和扶走阶段，跌落风险急剧增加。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '确保宝宝用来"巡航"的家具稳定，不易倾倒（测试方法：成人用手推不会翻倒）',
      '将婴儿床床板降到最低位置，确保床栏顶部高于宝宝站立时胸部',
      '窗户安装防护栏，不要将椅子或其它可攀爬的物品放在窗户下',
      '阳台和露台必须全封闭，或者每次使用时有成人全程陪同',
    ],
    riskWarning:
      '阳台和窗户跌落是婴幼儿严重意外伤害的主要原因之一。若住在高层，务必安装间距<10cm的窗护栏。不要在幼儿在场时开窗通风然后离开。',
  },
  {
    id: 'stage3-safety-3',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.SAFETY,
    title: '防止溺水和烫伤',
    description: '这两个隐形杀手在家中的危险程度比你想象的高。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '溺水可能发生在极少量水中：马桶、水桶、浴缸、宠物水碗甚至5cm深的水',
      '洗澡时全程陪同，永远不要让宝宝独自在浴室哪怕"只去拿毛巾"',
      '抱着宝宝时不要喝热饮、不要在炉边做饭；宝宝可能在你没注意时突然伸手',
      '家中热水器温度应设定在49°C以下，防止水龙头意外打开时烫伤',
    ],
    riskWarning:
      '浴室门随时关好。浴桶和水桶用完后立即倒空。如果在浴缸里接电话或应门，必须把宝宝也抱出来。不要相信"只离开一小会儿"——大多数婴儿溺水发生在5分钟内，且是在家人短暂离开时。',
  },

  // ==========================================
  // 7-12 个月 - 发育观察模块
  // ==========================================
  {
    id: 'stage3-dev-1',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.DEVELOPMENT,
    title: '大运动发育：从爬到走',
    description: '了解7-12月龄宝宝大运动发育里程碑，从爬行到扶站再到独立行走。',
    estimatedTime: 8,
    status: LevelStatus.LOCKED,
    order: 1,
    knowledgePoints: [
      '7-10个月：学会爬行（部分宝宝用屁股挪动或贴地蠕行，也属于正常变体）',
      '8-9个月：能不用支撑自己坐稳，并能从坐位变成俯卧位',
      '9-10个月：可以拉扶站立，并用双手扶着家具"巡航"走动',
      '12个月左右：部分宝宝可以独立站立数秒，甚至迈出第一步',
    ],
    riskWarning:
      '如果宝宝12个月时仍不能用四肢支撑身体、不会爬行或腹部贴地移动、不会扶着家具站立，应咨询儿科医生进行发育评估。正常运动的变体很多，但明显迟缓需要专业判断。',
  },
  {
    id: 'stage3-dev-2',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.DEVELOPMENT,
    title: '精细动作与认知发育',
    description: '了解宝宝手部精细动作的发展和认知能力的飞跃。',
    estimatedTime: 7,
    status: LevelStatus.LOCKED,
    order: 2,
    knowledgePoints: [
      '7-8个月：可以把物品从一只手换到另一只手，用手掌拍打水面或桌面',
      '9个月左右：出现钳状抓握——用食指和拇指捏起小物件（如小饼干）',
      '10-12个月：可以有意识地松手放开物品，把物品放进容器并取出',
      '认知发育方面：会寻找被藏起来的东西（客体永存性开始建立），模仿简单动作',
    ],
    riskWarning:
      '如果宝宝12个月时仍不会用拇指和食指捏物品、不会挥手再见、不会模仿简单动作（如拍手），需要就医做发育评估。注意：左右手偏好的出现应在1岁以后，1岁之前持续只用一只手可能提示对侧问题。',
  },
  {
    id: 'stage3-dev-3',
    stage: Stage.SEVEN_TO_TWELVE,
    module: Module.DEVELOPMENT,
    title: '语言与社交发育',
    description: '宝宝第一个词语快要来了！了解7-12月龄的语言和社交里程碑。',
    estimatedTime: 8,
    status: LevelStatus.LOCKED,
    order: 3,
    knowledgePoints: [
      '7-8个月：咿呀学语中开始出现重复音节，如"bababa""mamama"（尚无特定含义）',
      '9-10个月：开始理解简单词语（"不""再见"）并对自己的名字做出回应',
      '10-12个月：可能有意识地说出第一个有意义的词（通常是"妈妈""爸爸"）',
      '社交方面：8-9个月是分离焦虑和陌生人焦虑的高峰，这恰恰是正常发育的里程碑',
    ],
    riskWarning:
      '若宝宝12个月时对叫名字无反应、不发任何辅音+元音组合（如"ba""ma""da"）、不会用姿势沟通（指物、挥手再见、伸手要抱），应做听力检查和发育评估。不要轻信"男孩说话晚是正常的"——早期干预越早越好。',
  },
];
